import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Spinner,
  HStack,
  VStack,
  Badge,
  Divider,
  Checkbox,
  CheckboxGroup,
  Stack,
  Button,
} from '@chakra-ui/react';
import { ArrowBackIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getLocations } from '../../organization/api/organizationApi';
import { usePlaybackReport } from '../hooks/useSchedule';
import { VenueDateBar } from '../components/VenueDateBar';
import { todayStr, formatClock } from '../utils';
import type { PlaybackCategory } from '../types';

type TimelineRow =
  | { kind: 'entry'; at: string; title: string; subtitle: string; category: PlaybackCategory }
  | { kind: 'alert'; at: string; simulated: boolean }
  | { kind: 'block'; at: string; title: string };

const CATEGORY_COLOR: Record<PlaybackCategory, string> = { music: 'blue', ads: 'orange', special: 'purple' };

export function PlaybackReportPage() {
  const { t } = useTranslation();
  const today = todayStr();
  const [date, setDate] = useState(today);
  const [locationId, setLocationId] = useState('');
  const [categories, setCategories] = useState<PlaybackCategory[]>(['music', 'ads', 'special']);

  const { data: locations } = useQuery({ queryKey: ['organization', 'locations'], queryFn: getLocations });
  useEffect(() => {
    if (locations && locations.length && !locationId) setLocationId(locations[0].id);
  }, [locations, locationId]);

  const { data: report, isLoading } = usePlaybackReport(locationId || undefined, date, date <= today);

  const rows = useMemo<TimelineRow[]>(() => {
    if (!report) return [];
    const out: TimelineRow[] = [];
    for (const e of report.entries) {
      if (!categories.includes(e.category)) continue;
      out.push({
        kind: 'entry',
        at: e.playedAt,
        title: e.artist ? `${e.title} — ${e.artist}` : e.title,
        subtitle: e.sourcePlaylistName ?? e.sourceScenarioName ?? '',
        category: e.category,
      });
    }
    for (const a of report.alertEvents) out.push({ kind: 'alert', at: a.startedAt, simulated: a.isSimulated });
    for (const b of report.timeBlockMarkers) out.push({ kind: 'block', at: b.startsAt, title: b.scenarioName });
    return out.sort((x, y) => x.at.localeCompare(y.at));
  }, [report, categories]);

  return (
    <Box maxW="800px">
      <Button as={RouterLink} to="/schedule" variant="link" leftIcon={<ArrowBackIcon />} color="gray.500" mb={4}>
        {t('schedule.backToSchedule')}
      </Button>
      <Heading size="lg" color="gray.800" mb={4}>
        {t('schedule.playbackReport')}
      </Heading>

      {locations && (
        <VenueDateBar
          locations={locations}
          locationId={locationId}
          onLocationChange={setLocationId}
          date={date}
          onDateChange={setDate}
          canGoBack
          canGoForward={date < today}
        />
      )}

      <CheckboxGroup value={categories} onChange={(v) => setCategories(v as PlaybackCategory[])}>
        <Stack direction="row" spacing={6} mb={4}>
          <Checkbox value="music" colorScheme="brand">
            {t('schedule.music')}
          </Checkbox>
          <Checkbox value="ads" colorScheme="brand">
            {t('schedule.ads')}
          </Checkbox>
          <Checkbox value="special" colorScheme="brand">
            {t('schedule.special')}
          </Checkbox>
        </Stack>
      </CheckboxGroup>

      {report && (
        <HStack mb={3} spacing={4} color="gray.500" fontSize="sm">
          <Text>{t('schedule.totalPlayed', { count: report.totals.count })}</Text>
        </HStack>
      )}

      <Divider mb={2} />

      {isLoading ? (
        <Flex justify="center" py={16}>
          <Spinner size="xl" color="brand.500" thickness="3px" />
        </Flex>
      ) : !report ? (
        <Text py={10} color="gray.400" textAlign="center">
          {t('schedule.reportUnavailable')}
        </Text>
      ) : rows.length === 0 ? (
        <Text py={12} color="gray.400" textAlign="center">
          {t('schedule.noPlayback')}
        </Text>
      ) : (
        <VStack align="stretch" spacing={0} divider={<Divider />}>
          {rows.map((r, i) =>
            r.kind === 'alert' ? (
              <HStack key={i} py={2.5} px={2} spacing={3} bg="red.50" borderRadius="md">
                <Text w="56px" fontSize="sm" color="gray.500" flexShrink={0}>
                  {formatClock(r.at)}
                </Text>
                <Text color="red.600" fontWeight="600">
                  {t('schedule.alertEvent')}
                </Text>
                {r.simulated && <Badge colorScheme="gray">{t('schedule.simulated')}</Badge>}
              </HStack>
            ) : r.kind === 'block' ? (
              <HStack key={i} py={2} px={2} spacing={3}>
                <Text w="56px" fontSize="sm" color="gray.400" flexShrink={0}>
                  {formatClock(r.at)}
                </Text>
                <Text fontSize="sm" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                  {r.title}
                </Text>
              </HStack>
            ) : (
              <HStack key={i} py={2.5} px={2} spacing={3} _hover={{ bg: 'gray.50' }} borderRadius="md">
                <Text w="56px" fontSize="sm" color="gray.400" flexShrink={0}>
                  {formatClock(r.at)}
                </Text>
                <Box flex={1} minW={0}>
                  <Text fontSize="sm" color="gray.800" noOfLines={1}>
                    {r.title}
                  </Text>
                  {r.subtitle && (
                    <Text fontSize="xs" color="gray.400" noOfLines={1}>
                      {r.subtitle}
                    </Text>
                  )}
                </Box>
                <Badge colorScheme={CATEGORY_COLOR[r.category]} fontSize="0.6rem">
                  {t(`schedule.${r.category}`)}
                </Badge>
              </HStack>
            ),
          )}
        </VStack>
      )}
    </Box>
  );
}

export default PlaybackReportPage;
